import { DownloadOutlined, EyeOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Empty, Space, Typography } from "antd";
import { getUrl } from "aws-amplify/storage";
import AttachmentThumbnail from "components/atoms/AttachmentThumbnail";
import { useCallback, useState } from "react";

const { Text, Title } = Typography;

const InlineAttachmentViewer = ({
    files = [],
    onPreview,
    onDownload,
    layout = "carousel", // 'grid' or 'carousel'
    showControls = true,
    maxThumbnails = 6,
    className = "",
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [previewingFile, setPreviewingFile] = useState(null);

    const handlePreview = useCallback(
        async (file, event) => {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            setPreviewingFile(file);
            if (onPreview) {
                onPreview(file);
            }
        },
        [onPreview],
    );

    const handleDownload = useCallback(
        async (file) => {
            try {
                const urlResult = await getUrl({ path: `public/${file.key}` });
                window.open(urlResult.url.toString(), "_blank");

                if (onDownload) {
                    onDownload(file);
                }
            } catch (error) {
                console.error("Download failed:", error);
            }
        },
        [onDownload],
    );

    const nextFile = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % files.length);
    }, [files.length]);

    const prevFile = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + files.length) % files.length);
    }, [files.length]);

    if (files.length === 0) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No attachments" className="py-8" />;
    }

    const displayFiles = layout === "carousel" ? files : files.slice(0, maxThumbnails);
    const hasMoreFiles = files.length > maxThumbnails;

    if (layout === "carousel") {
        return (
            <div className={`inline-attachment-carousel ${className}`}>
                <div className="mb-4 flex items-center justify-between">
                    <Title level={5} className="mb-0">
                        Attachments ({files.length})
                    </Title>
                    {showControls && files.length > 1 && (
                        <Space>
                            <Button icon={<LeftOutlined />} size="small" onClick={prevFile} disabled={files.length <= 1} />
                            <Text className="text-sm text-gray-500">
                                {currentIndex + 1} of {files.length}
                            </Text>
                            <Button icon={<RightOutlined />} size="small" onClick={nextFile} disabled={files.length <= 1} />
                        </Space>
                    )}
                </div>

                <div className="relative px-6 py-4">
                    <div className="grid place-items-center">
                        <AttachmentThumbnail
                            file={displayFiles[currentIndex]}
                            size="large"
                            onPreview={handlePreview}
                            className="!m-4 rounded-lg shadow-sm transition-all duration-300"
                        />
                    </div>

                    {files.length > 1 && (
                        <div className="mt-6 overflow-x-auto">
                            <div className="flex snap-x snap-mandatory gap-4 px-2">
                                {files.map((f, i) => (
                                    <button
                                        key={f.key || i}
                                        onClick={() => setCurrentIndex(i)}
                                        className={`shrink-0 snap-start rounded-lg ring-1 ring-gray-200 ${i === currentIndex ? "ring-2 ring-blue-500" : ""}`}
                                    >
                                        <AttachmentThumbnail file={f} size="medium" onPreview={handlePreview} className="!m-0" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {showControls && (
                        <div className="mt-4 flex justify-center gap-2">
                            <Button icon={<EyeOutlined />} size="small" onClick={(e) => handlePreview(displayFiles[currentIndex], e)}>
                                Preview
                            </Button>
                            <Button icon={<DownloadOutlined />} size="small" onClick={() => handleDownload(displayFiles[currentIndex])}>
                                Download
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={`inline-attachment-grid ${className}`}>
            {hasMoreFiles && (
                <div className="mb-4 text-right">
                    <Text className="text-sm text-gray-500">
                        Showing {maxThumbnails} of {files.length}
                    </Text>
                </div>
            )}

            <div className="px-6 py-4">
                <div className="grid grid-cols-2 gap-12 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                    {displayFiles.map((file, index) => (
                        <div key={file.key || index} className="group relative mb-6">
                            <div className="relative mx-2 w-full">
                                <AttachmentThumbnail file={file} size="medium" onPreview={handlePreview} className="w-full" />

                                {showControls && (
                                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black bg-opacity-60 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                        <Space size="small">
                                            <Button icon={<EyeOutlined />} size="small" type="primary" ghost onClick={(e) => handlePreview(file, e)} className="px-2 py-1 text-xs">
                                                View
                                            </Button>
                                            <Button
                                                icon={<DownloadOutlined />}
                                                size="small"
                                                type="primary"
                                                ghost
                                                onClick={() => handleDownload(file)}
                                                className="px-2 py-1 text-xs"
                                            >
                                                Download
                                            </Button>
                                        </Space>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {hasMoreFiles && (
                <div className="mt-4 text-center">
                    <Text className="text-sm text-gray-500">+{files.length - maxThumbnails} more files</Text>
                </div>
            )}
        </div>
    );
};

export default InlineAttachmentViewer;
